import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export interface BackLinkEntry {
  to: string;
  label: ReactNode;
}

interface PageHeaderContextValue {
  backLink: BackLinkEntry | null;
  setBackLink: (link: BackLinkEntry | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

/**
 * Provider for the persistent page header slot rendered above `<main>` by
 * `ConsoleLayout`. Detail pages register a back link via `useSetPageBackLink`
 * and it appears in the reserved slot at the top of the page — outside the
 * main's padding zone, so the page title's Y position is unaffected.
 */
export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [backLink, setBackLink] = useState<BackLinkEntry | null>(null);
  return (
    <PageHeaderContext.Provider value={{ backLink, setBackLink }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageBackLink(): BackLinkEntry | null {
  return useContext(PageHeaderContext)?.backLink ?? null;
}

/**
 * Register a back link for the duration of the calling component's mount.
 * Pass `null` (or simply unmount) to clear it. The label can be any
 * ReactNode so callers can include badges/icons if needed.
 */
export function useSetPageBackLink(link: BackLinkEntry | null) {
  const ctx = useContext(PageHeaderContext);
  const setBackLink = ctx?.setBackLink;

  const to = link?.to;
  const label = link?.label;

  const apply = useCallback(() => {
    if (!setBackLink) return;
    if (to) setBackLink({ to, label });
    else setBackLink(null);
  }, [setBackLink, to, label]);

  useEffect(() => {
    apply();
    return () => {
      setBackLink?.(null);
    };
  }, [apply, setBackLink]);
}
